/**
 * The desktop hover language, in one place.
 *
 * Every hover treatment in the app is one of these kinds:
 *   card   clickable cards and tiles: border + shadow + 1px lift
 *   row    rows, nav items, tabs, chips, menu items, icon buttons: background tint
 *   button filled CTAs: shadow + 1px lift (fill unchanged)
 *   link   text actions: ink moves to interactive.primaryHover (via textStyle)
 *   glass  glass-filled buttons (rest fill glass.buttonBg): fill → glass.buttonBgHover + border → card.borderHover
 *   field  form-field wrappers: border only, cursor untouched
 *   none   cursor + transition only (an active tab or selected row that must not tint)
 *
 * `row` WRITES backgroundColor, so it is for surfaces whose rest fill is
 * transparent; a control with its own fill uses `glass` (translucent fill),
 * `field` (opaque fill, border cue) or `button` (opaque fill, elevation).
 *
 * Web-only. Off web (or when disabled) everything is inert: empty handler
 * objects and null styles, so native renders exactly as before. `Platform.OS`
 * is read at render time on purpose: tests assign `Platform.OS = 'web'` before
 * mounting, which an import-time read (WEB_STYLES, useHover) would ignore.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform } from 'react-native';
import { useThemeColors } from '@/hooks/shared/useThemeColors';
import { useReducedMotion } from '@/hooks/shared/useReducedMotion';
import { buildCardHoverStyle } from '@/components/shared/redesign/buildCardHoverStyle';

export type HoverKind = 'card' | 'row' | 'button' | 'link' | 'glass' | 'field' | 'none';

export interface HoverStyleOptions {
  /** Disables hover along with the press; also clears an in-flight hover. */
  disabled?: boolean;
  /** Lift in px for `card` and `button`. Default 1. */
  liftPx?: number;
}

export interface HoverStyleResult {
  isHovered: boolean;
  /** Spread onto a Pressable. */
  hoverProps: { onHoverIn?: () => void; onHoverOut?: () => void };
  /** Spread (cast to any) onto a plain View; react-native-web forwards them to the DOM. */
  mouseProps: { onMouseEnter?: () => void; onMouseLeave?: () => void };
  /** Append to the element's style array. */
  viewStyle: any[] | null;
  /** `link` only: append to the Text's style array. */
  textStyle: any[] | null;
}

// Only the properties this language animates. Never `all`: a layout change on
// a hovered element must not animate by accident.
export const HOVER_TRANSITION =
  'background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease, opacity 0.15s ease, color 0.15s ease';

const INERT: HoverStyleResult = { isHovered: false, hoverProps: {}, mouseProps: {}, viewStyle: null, textStyle: null };

export function useHoverStyle(kind: HoverKind, options: HoverStyleOptions = {}): HoverStyleResult {
  const { disabled = false, liftPx = 1 } = options;
  const { colors, semantic } = useThemeColors();
  const reduceMotion = useReducedMotion();
  const [hovered, setHovered] = useState(false);

  const onHoverIn = useCallback(() => setHovered(true), []);
  const onHoverOut = useCallback(() => setHovered(false), []);

  // react-native-web removes a disabled Pressable's hover listeners, so a
  // hover that was in flight when `disabled` flipped would never end.
  useEffect(() => {
    if (disabled) setHovered(false);
  }, [disabled]);

  const isWeb = Platform.OS === 'web';
  const active = isWeb && !disabled;
  const isHovered = active && hovered;

  return useMemo<HoverStyleResult>(() => {
    if (!active) return INERT;

    const hoverProps = { onHoverIn, onHoverOut };
    const mouseProps = { onMouseEnter: onHoverIn, onMouseLeave: onHoverOut };
    // userSelect: TouchableOpacity set user-select:none on web; Pressable does
    // not, so without this a double-click selects a button's label.
    const rest: any[] = kind === 'field' ? [{ transition: HOVER_TRANSITION }] : [{ cursor: 'pointer', transition: HOVER_TRANSITION, userSelect: 'none' }];
    const textRest: any[] | null = kind === 'link' ? [{ transition: 'color 0.15s ease' }] : null;

    if (!isHovered) {
      return { isHovered, hoverProps, mouseProps, viewStyle: rest, textStyle: textRest };
    }

    // Each kind reads only its own tokens, and only once hovered: a component
    // test with a partial theme mounts without the hover tokens.
    const elevation = () =>
      buildCardHoverStyle({ isHovered: true, reduceMotion, isWeb: true, shadowHover: colors.card.shadowHover, liftPx }) ?? [];
    const byKind = (): any[] => {
      switch (kind) {
        case 'card':
          return [{ borderColor: colors.card.borderHover }, ...elevation()];
        case 'row':
          return [{ backgroundColor: semantic.background.hover }];
        case 'button':
          return elevation();
        case 'glass':
          return [{ backgroundColor: colors.glass.buttonBgHover, borderColor: colors.card.borderHover }];
        case 'field':
          return [{ borderColor: colors.card.borderHover }];
        default:
          return [];
      }
    };

    return {
      isHovered,
      hoverProps,
      mouseProps,
      viewStyle: [...rest, ...byKind()],
      // Ink shift plus underline: in dark the link ink already equals
      // primaryHover, so the underline is the cue that survives both themes.
      textStyle: kind === 'link' ? [...(textRest ?? []), { color: semantic.interactive.primaryHover, textDecorationLine: 'underline' }] : null,
    };
  }, [active, isHovered, kind, liftPx, reduceMotion, colors.card?.shadowHover, colors.card?.borderHover, colors.glass?.buttonBgHover, semantic?.background?.hover, semantic?.interactive?.primaryHover, onHoverIn, onHoverOut]);
}
